import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as Tabs from '@radix-ui/react-tabs';
import { AlertTriangle, Plus, Square, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  formatDateTime,
  formatNumber,
  formatRate,
  messages,
  type CampaignSummary,
} from '@nesc/shared';
import { api, errorMessage, type ListResponse } from '@/lib/api-client';
import { qk } from '@/lib/query-client';
import { useAuth } from '@/lib/auth';
import { usePollingInterval, useUrlFilters } from '@/lib/hooks';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/common/status-badge';
import { ConfirmDialog, EmptyState } from '@/components/common/feedback';
import { ResponsiveList, RowChevron, type Column } from '@/components/common/responsive-list';
import { PageHeader } from '@/components/common/page';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { cn, tabTriggerClass } from '@/lib/utils';

const TABS = [
  { value: '', label: messages.common.all },
  { value: 'running', label: '実行中' },
  { value: 'scheduled', label: '予約' },
  { value: 'draft', label: '下書き' },
  { value: 'completed', label: '完了' },
  { value: 'canceled', label: '中止' },
];

/** 削除できる状態。進行中のものは先に中止する（docs/05-api-spec.md 第9章） */
const DELETABLE_STATUSES = ['draft', 'canceled', 'completed'];

/** 一覧内の細い進捗バー。カードの高さを抑えるため JobProgress ではなくこれを使う */
const MiniBar = ({ done, total, full }: { done: number; total: number; full?: boolean }) => {
  const percent = total > 0 ? Math.min(Math.round((done / total) * 100), 100) : 0;
  return (
    <div className="flex min-w-0 items-center gap-2">
      <div
        className={cn('h-1.5 shrink-0 overflow-hidden rounded-control bg-muted', full ? 'w-full flex-1' : 'w-16')}
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div className="h-full bg-foreground transition-all" style={{ width: `${percent}%` }} />
      </div>
      <span className="tabular shrink-0 text-note text-foreground-muted">{percent}%</span>
    </div>
  );
};

/** S40 キャンペーン一覧 */
export const CampaignsPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasRole } = useAuth();
  const { filters, setFilter } = useUrlFilters({ status: '' });
  const [pauseTarget, setPauseTarget] = useState<CampaignSummary | null>(null);
  const [emergencyOpen, setEmergencyOpen] = useState(false);
  const [emergencyReason, setEmergencyReason] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<CampaignSummary | null>(null);

  const query = { status: filters.status || undefined, limit: 30 };

  const { data, isLoading } = useQuery({
    queryKey: qk.campaigns(query),
    queryFn: () => api.get<ListResponse<CampaignSummary>>('/campaigns', query),
    refetchInterval: usePollingInterval(true, 10_000),
  });

  const pause = useMutation({
    mutationFn: (id: string) => api.post(`/campaigns/${id}/pause`, {}),
    onSuccess: () => {
      toast.success('一時停止しました', { description: messages.campaign.confirmPause });
      void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      setPauseTarget(null);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const resume = useMutation({
    mutationFn: (id: string) => api.post(`/campaigns/${id}/resume`),
    onSuccess: () => {
      toast.success('再開しました');
      void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/campaigns/${id}`),
    onSuccess: () => {
      toast.success('キャンペーンを削除しました');
      void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      setDeleteTarget(null);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const emergencyStop = useMutation({
    mutationFn: () => api.post('/emergency-stop', { reason: emergencyReason }),
    onSuccess: (result) => {
      const stopped = (result as { data: { stopped: number } }).data.stopped;
      toast.success(`${stopped} 件のキャンペーンを停止しました`);
      void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      setEmergencyOpen(false);
      setEmergencyReason('');
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const atDailyLimit = (c: CampaignSummary) => c.status === 'running' && c.todaySent >= c.dailyLimit;

  /** 送信結果の内訳。要対応・結果不明は 0 以外のときだけ強調表示する */
  const resultSummary = (c: CampaignSummary) =>
    c.counts.total > 0 ? (
      <span className="tabular flex flex-wrap items-center gap-x-2 gap-y-0.5 text-note text-foreground-muted">
        <span>成 {c.counts.success}</span>
        <span>失 {c.counts.failed}</span>
        {c.counts.unknown > 0 && <span className="font-semibold text-foreground">不明 {c.counts.unknown}</span>}
        {c.counts.needsAction > 0 && <span className="font-semibold text-foreground">要対応 {c.counts.needsAction}</span>}
      </span>
    ) : (
      <span className="text-note text-foreground-subtle">—</span>
    );

  const columns: Column<CampaignSummary>[] = [
    {
      key: 'name',
      header: 'キャンペーン',
      render: (c) => (
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate font-medium text-foreground">{c.name}</span>
          <StatusBadge kind="campaign_status" value={c.status} />
        </div>
      ),
    },
    {
      key: 'list',
      header: 'リスト',
      width: '160px',
      render: (c) => (
        <span
          className="block truncate text-note text-foreground-muted"
          title={c.product?.name ? `商材: ${c.product.name}` : undefined}
        >
          {c.list?.name ?? '—'}
        </span>
      ),
    },
    {
      key: 'target',
      header: '対象',
      width: '72px',
      align: 'right',
      render: (c) => (
        <span className="tabular text-note text-foreground-muted">
          {c.counts.total > 0 ? formatNumber(c.counts.total) : '—'}
        </span>
      ),
    },
    {
      key: 'progress',
      header: '進捗',
      width: '150px',
      render: (c) =>
        c.counts.total > 0 ? (
          <div className="flex items-center gap-2">
            <MiniBar done={c.counts.done + c.counts.skipped} total={c.counts.total} />
            {atDailyLimit(c) && (
              <span
                className="shrink-0 rounded-control border border-border-strong px-1 text-note text-foreground-muted"
                title={messages.campaign.dailyLimitReached(
                  c.dailyLimit,
                  c.nextRunAt ? formatDateTime(c.nextRunAt) : '次の送信可能時間帯',
                )}
              >
                上限
              </span>
            )}
          </div>
        ) : (
          <span className="text-note text-foreground-subtle">—</span>
        ),
    },
    { key: 'result', header: '結果', width: '180px', render: resultSummary },
    {
      key: 'reaction',
      header: '反応',
      width: '140px',
      align: 'right',
      render: (c) => (
        <span className="tabular text-note text-foreground-muted">
          返信 {formatRate(c.replyRate)}・アポ {c.appointmentCompanies}
        </span>
      ),
    },
  ];

  const rowActions = (c: CampaignSummary) => (
    <div className="flex items-center justify-end gap-1">
      {hasRole('manager') && c.status === 'running' && (
        <Button variant="ghost" size="sm" onClick={() => setPauseTarget(c)}>
          <Square aria-hidden className="size-4" />
          {messages.campaign.pause}
        </Button>
      )}
      {hasRole('manager') && c.status === 'paused' && (
        <Button variant="ghost" size="sm" onClick={() => resume.mutate(c.id)}>
          {messages.campaign.resume}
        </Button>
      )}
      {hasRole('manager') && DELETABLE_STATUSES.includes(c.status) && (
        <Button variant="ghost" size="icon-sm" aria-label={messages.common.delete} onClick={() => setDeleteTarget(c)}>
          <Trash2 aria-hidden className="size-4" />
        </Button>
      )}
      <RowChevron />
    </div>
  );

  const card = (c: CampaignSummary) => (
    <button
      type="button"
      onClick={() => navigate(`/campaigns/${c.id}`)}
      className="flex w-full flex-col gap-2 text-left"
    >
      <div className="flex items-start justify-between gap-2">
        <span className="min-w-0 truncate text-body font-medium">{c.name}</span>
        <StatusBadge kind="campaign_status" value={c.status} />
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-note text-foreground-muted">
        <span className="min-w-0 truncate">{c.list?.name ?? '—'}</span>
        {c.counts.total > 0 && <span className="tabular shrink-0">対象 {formatNumber(c.counts.total)}</span>}
        {atDailyLimit(c) && (
          <span className="shrink-0 rounded-control border border-border-strong px-1">本日上限</span>
        )}
      </div>
      {c.counts.total > 0 && <MiniBar done={c.counts.done + c.counts.skipped} total={c.counts.total} full />}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {resultSummary(c)}
        <span className="tabular text-note text-foreground-muted">
          返信 {formatRate(c.replyRate)}・アポ {c.appointmentCompanies}
        </span>
      </div>
    </button>
  );

  return (
    <>
      <PageHeader
        title={messages.campaign.title}
        actions={
          <>
            {hasRole('admin') && (
              <Button variant="outline" onClick={() => setEmergencyOpen(true)}>
                <AlertTriangle aria-hidden />
                {messages.campaign.emergencyStop}
              </Button>
            )}
            {hasRole('manager') && (
              <Button onClick={() => navigate('/campaigns/new')}>
                <Plus aria-hidden />
                {messages.campaign.create}
              </Button>
            )}
          </>
        }
      />

      <Tabs.Root value={filters.status as string} onValueChange={(v) => setFilter('status', v)}>
        <Tabs.List className="scroll-x mb-4 flex gap-1 border-b border-border" aria-label="状態で絞り込む">
          {TABS.map((item) => (
            <Tabs.Trigger key={item.value || 'all'} value={item.value} className={tabTriggerClass}>
              {item.label}
            </Tabs.Trigger>
          ))}
        </Tabs.List>

        <Tabs.Content value={filters.status as string}>
          <ResponsiveList
            columns={columns}
            rows={data?.data ?? []}
            isLoading={isLoading}
            density="compact"
            onRowClick={(c) => navigate(`/campaigns/${c.id}`)}
            rowActions={rowActions}
            card={card}
            empty={
              <EmptyState
                title={messages.empty.campaigns.title}
                description={messages.empty.campaigns.description}
                action={
                  hasRole('manager')
                    ? { label: messages.empty.campaigns.action, onClick: () => navigate('/campaigns/new') }
                    : undefined
                }
              />
            }
          />
        </Tabs.Content>
      </Tabs.Root>

      <ConfirmDialog
        open={!!pauseTarget}
        onOpenChange={(open) => !open && setPauseTarget(null)}
        title={messages.campaign.pause}
        summary={`「${pauseTarget?.name}」を一時停止します。`}
        detail={<p className="text-body text-foreground-muted">{messages.campaign.confirmPause}</p>}
        confirmText={messages.campaign.pause}
        onConfirm={() => pauseTarget && pause.mutate(pauseTarget.id)}
        isPending={pause.isPending}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={messages.campaign.remove}
        summary={messages.campaign.confirmRemove(deleteTarget?.name ?? '')}
        detail={<p className="text-body text-foreground-muted">{messages.campaign.removeNote}</p>}
        confirmText={messages.common.delete}
        irreversible
        onConfirm={() => deleteTarget && remove.mutate(deleteTarget.id)}
        isPending={remove.isPending}
      />

      <ConfirmDialog
        open={emergencyOpen}
        onOpenChange={setEmergencyOpen}
        title={messages.campaign.emergencyStop}
        summary="進行中のすべてのキャンペーンを即時停止します。"
        detail={
          <Field label={messages.common.reason} required>
            {(props) => (
              <Input
                {...props}
                value={emergencyReason}
                onChange={(e) => setEmergencyReason(e.target.value)}
                placeholder="誤送信の疑いがあるため"
              />
            )}
          </Field>
        }
        confirmText={messages.campaign.emergencyStop}
        irreversible
        onConfirm={() => emergencyStop.mutate()}
        isPending={emergencyStop.isPending}
      />
    </>
  );
};
